import React, { useState } from 'react';
import useFetchRCA from './hooks/useFetchRCA';
import { addRCADimension, formatMultiData, formatRCAColumns } from './hooks/utils';
import { getMetricFilterMetric } from '@src/utils/table/mfUtils';
import { metricKeys } from '@pulse/metrics';
import { Retry } from '@src/components/common';
import Table from '@src/components/common/Table';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { useWidget } from '../../state/dashboard';
import { Button, Space, Tooltip } from 'antd';
import Drilldown from '../../control/Drilldown';
import { EditOutlined } from '@ant-design/icons';
import RemoveWidget from '../../control/RemoveWidget';
import EditRCA from './EditRCA';
import useDrillDownToPreset from '../../hooks/useDrilldownToPreset';
import { useValidateParams } from './hooks/useValidateParams';
import WidgetWarning from '../../common/WidgetWarning';

function AutoRCA({ wid }) {
  const widgetHandler = useWidget({ wid });
  const [editMode, setEditMode] = useState(false);
  const { lowerBound } = widgetHandler.state;

  const { isIntervalValid, message } = useValidateParams({});

  const handleAction = useDrillDownToPreset({ category: 'network' });

  const handleEditClick = () => {
    setEditMode((v) => !v);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSave = (newState) => {
    widgetHandler.update(newState);
    handleCancel();
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title="Network: Auto RCA"
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          {isIntervalValid && (
            <>
              <Tooltip title="Edit">
                <Button icon={<EditOutlined />} onClick={handleEditClick} type="text" />
              </Tooltip>
              <Drilldown createDashboard={handleAction} />
            </>
          )}
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      {isIntervalValid ? (
        editMode ? (
          <EditRCA lowerBound={lowerBound} onSave={handleSave} onCancel={handleCancel} />
        ) : (
          <RCATable wid={wid} lowerBound={lowerBound} />
        )
      ) : (
        <WidgetWarning message={message} />
      )}
    </WidgetCardWrapper>
  );
}

function RCATable({ wid, lowerBound }) {
  const [sortBy, setSortBy] = useState({
    key: `${getMetricFilterMetric(metricKeys.AverageRequestDuration)}`,
    order: 'desc',
  });
  const { data, isFetching, refetch, isError, queued } = useFetchRCA({ wid, lowerBound });
  const { rows } = formatMultiData({ data, sortBy, searchText: '' });
  const tableRows = addRCADimension(rows, data?.rcaData);
  const columns = formatRCAColumns();

  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };

  return (
    <Retry
      fetchData={refetch}
      isLoading={isFetching}
      showError={isError}
      isEmpty={tableRows.length === 0}
      progress={true}
      queued={queued}
    >
      <Table
        style={{ height: '100%', flexGrow: 1 }}
        columns={columns}
        data={tableRows}
        headerHeight={35}
        rowHeight={35}
        sortBy={sortBy}
        onColumnSort={onColumnSort}
        rowKey={'network_api_calls'}
      />
    </Retry>
  );
}

export default AutoRCA;
